import { Children, Fragment, forwardRef, isValidElement, type ReactNode, useState } from 'react';
import {
  type LayoutChangeEvent,
  Platform,
  View,
  type ViewProps,
  type ViewStyle,
} from 'react-native';
import { type Breakpoint, useResponsive } from '../../hooks/useResponsive';
import { type SpacingScale, spacing as spacingValues } from '../../theme/spacing';
import { tokens } from '../../theme/tokens';
import { Box } from './Box';

interface GridProps extends Omit<ViewProps, 'children'> {
  children?: ReactNode;
  columns?: number | { mobile?: number; tablet?: number; desktop?: number };
  spacing?: SpacingScale;
  columnSpacing?: SpacingScale;
  rowSpacing?: SpacingScale;
  minChildWidth?: number;
  maxChildWidth?: number;
}

type WebGridItemStyle = ViewStyle & {
  gridColumn?: string;
  gridRow?: string;
};

function getBreakpoint(width: number): Breakpoint {
  if (width >= tokens.breakpoints.wide) return 'wide';
  if (width >= tokens.breakpoints.desktop) return 'desktop';
  if (width >= tokens.breakpoints.tablet) return 'tablet';
  return 'mobile';
}

export const Grid = forwardRef<View, GridProps>(
  (
    {
      children,
      columns = 1,
      spacing,
      columnSpacing,
      rowSpacing,
      minChildWidth,
      maxChildWidth,
      style,
      onLayout,
      ...rest
    },
    ref,
  ) => {
    const { breakpoint } = useResponsive();
    const [containerWidth, setContainerWidth] = useState<number>();
    const activeBreakpoint =
      Platform.OS === 'web' && containerWidth !== undefined
        ? getBreakpoint(containerWidth)
        : breakpoint;

    const getColumns = () => {
      if (typeof columns === 'number') return columns;

      switch (activeBreakpoint) {
        case 'desktop':
        case 'wide':
          return columns.desktop ?? columns.tablet ?? columns.mobile ?? 1;
        case 'tablet':
          return columns.tablet ?? columns.mobile ?? 1;
        default:
          return columns.mobile ?? 1;
      }
    };

    const columnCount = getColumns();
    const childrenArray = Children.toArray(children);

    const handleLayout = (event: LayoutChangeEvent) => {
      setContainerWidth(event.nativeEvent.layout.width);
      onLayout?.(event);
    };

    if (Platform.OS === 'web') {
      const gapValue = spacing ? spacingValues[spacing] : undefined;
      const columnGapValue = columnSpacing ? spacingValues[columnSpacing] : undefined;
      const rowGapValue = rowSpacing ? spacingValues[rowSpacing] : undefined;

      // Use CSS Grid on web for better performance
      return (
        <Box
          ref={ref}
          onLayout={handleLayout}
          style={[
            {
              display: 'grid',
              minWidth: 0,
              width: '100%',
              gridTemplateColumns: (() => {
                if (!minChildWidth) return `repeat(${columnCount}, minmax(0, 1fr))`;
                const maxCol = maxChildWidth ? `${maxChildWidth}px` : '1fr';
                return `repeat(auto-fit, minmax(${minChildWidth}px, ${maxCol}))`;
              })(),
              gap: gapValue,
              columnGap: columnGapValue,
              rowGap: rowGapValue,
            } as unknown as ViewStyle,
            style,
          ]}
          {...rest}
        >
          {children}
        </Box>
      );
    }

    // Flexbox fallback for native platforms.
    // Yoga's `gap` is unreliable with `flexWrap` on iOS, so we apply spacing
    // via explicit per-cell padding. `spacing` acts as fallback when only the
    // single `spacing` prop is set (no separate column/row spacing).
    const effectiveColumnSpacing = columnSpacing ?? spacing;
    const effectiveRowSpacing = rowSpacing ?? spacing;

    // Single-column native: paddingBottom on a flex-wrap child renders unreliably
    // on iOS Yoga. Use VStack-style explicit spacer Views (matches Stack.tsx
    // pattern for `useSpacers = Platform.OS !== 'web'`) so gaps render correctly.
    if (columnCount === 1 && effectiveRowSpacing) {
      const spacerHeight = spacingValues[effectiveRowSpacing];
      return (
        <Box
          ref={ref}
          flexDirection="column"
          style={[{ minWidth: 0, width: '100%' }, style]}
          onLayout={onLayout}
          {...rest}
        >
          {childrenArray.map((child, index) => {
            const childKey = isValidElement(child)
              ? (child.key ?? `grid-cell-${index}`)
              : `grid-cell-${index}`;
            return (
              <Fragment key={childKey}>
                {index > 0 ? <View style={{ height: spacerHeight, flexShrink: 0 }} /> : null}
                {child}
              </Fragment>
            );
          })}
        </Box>
      );
    }

    return (
      <Box
        ref={ref}
        flexDirection="row"
        flexWrap="wrap"
        style={[{ minWidth: 0, width: '100%' }, style]}
        onLayout={onLayout}
        {...rest}
      >
        {childrenArray.map((child, index) => (
          <Box
            key={isValidElement(child) ? (child.key ?? `grid-cell-${index}`) : `grid-cell-${index}`}
            width={`${100 / columnCount}%`}
            minWidth={0}
            paddingRight={
              effectiveColumnSpacing && (index + 1) % columnCount !== 0
                ? effectiveColumnSpacing
                : undefined
            }
            paddingBottom={
              effectiveRowSpacing && index < childrenArray.length - columnCount
                ? effectiveRowSpacing
                : undefined
            }
          >
            {child}
          </Box>
        ))}
      </Box>
    );
  },
);

Grid.displayName = 'Grid';

interface GridItemProps extends ViewProps {
  colSpan?: number | { mobile?: number; tablet?: number; desktop?: number };
  rowSpan?: number;
}

export const GridItem = forwardRef<View, GridItemProps>(
  ({ colSpan, rowSpan, style, children, ...rest }, ref) => {
    const { breakpoint } = useResponsive();

    const getColSpan = () => {
      if (typeof colSpan === 'number' || colSpan === undefined) return colSpan;

      switch (breakpoint) {
        case 'desktop':
        case 'wide':
          return colSpan.desktop ?? colSpan.tablet ?? colSpan.mobile;
        case 'tablet':
          return colSpan.tablet ?? colSpan.mobile;
        default:
          return colSpan.mobile;
      }
    };

    const span = getColSpan();

    if (Platform.OS === 'web') {
      return (
        <Box
          ref={ref}
          style={[
            {
              gridColumn: span ? `span ${span}` : undefined,
              gridRow: rowSpan ? `span ${rowSpan}` : undefined,
              minWidth: 0,
            } as WebGridItemStyle,
            style,
          ]}
          {...rest}
        >
          {children}
        </Box>
      );
    }

    // For native platforms, GridItem acts as a regular Box
    return (
      <Box ref={ref} style={[{ minWidth: 0 }, style]} {...rest}>
        {children}
      </Box>
    );
  },
);

GridItem.displayName = 'GridItem';
